import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatSidenav } from '@angular/material/sidenav';
import { Router, UrlTree } from '@angular/router';
import { EMPTY } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/core/security/auth.service';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';
import { Empresa } from '../../models/empresa.model';
import { EmailTemplates } from '../../models/email.model';
import { UsuarioAuth, Usuario } from '../../models/usuario.model';
import { EmpresaService } from '../../services/empresa.service';
import { EnviarEmailService } from '../../services/enviar-email.service';
import { RegistroService } from '../../services/registro.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'dpc-sidenav',
    templateUrl: './dpc-sidenav.component.html',
    styleUrls: ['./dpc-sidenav.component.scss'],
    standalone: false
})
export class HostSidenavComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('selectEmpresas') empresasSelect: MatSelect;

  submenu = '';

  public appName = environment.name;
  public appVersion = environment.version;
  public environment = environment.production ? 'p' : 'd';
  public projectId = environment.firebaseConfig.projectId;

  public avisoCerrado = false;
  public resendEmailInFlight = false;
  public resendEmailCountdown = 0;
  private resendEmailTimer: ReturnType<typeof setInterval> | null = null;
  private readonly resendEmailCooldownSeconds = 90;

  public subs = new SubSink();
  public esMaster = false;
  public empresas: Empresa[];

  public user$ = this.auth.user$;

  data$ = this.auth.esMaster$
    .pipe(
      switchMap((esMaster) => {
        this.esMaster = esMaster;
        if (!esMaster) {
          return EMPTY;
        }
        if (esMaster) {
          return this.empresasSvc.getTodasEmpresas();
        }
      })
    )
    .pipe(
      tap((empresas) => {
        this.empresas = empresas;
        // console.log(this.empresasSelect);
      })
    );

  public empresaSelGlobal$ = this.auth.empresaSelGlobal$;

  constructor(
    private auth: AuthService,
    private router: Router,
    private empresasSvc: EmpresaService,
    private registroService: RegistroService,
    private emailService: EnviarEmailService,
    private snackBar: MatSnackBar
  ) {}

  public onSubMenu(menu: string) {
    console.log(this.submenu, menu);
    if (this.submenu === menu) {
      this.submenu = '';
    } else {
      this.submenu = menu;
    }
  }

  public async onNavigate(path: (string | UrlTree)[], event?: Event): Promise<void> {
    event?.stopPropagation();
    await this.router.navigate(path);
    this.sidenav.close();
  }

  ngOnInit() {
    this.subs.sink = this.data$.subscribe();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.clearResendEmailTimer();
  }

  async onSelectEmpresa(event: MatSelectChange) {
    this.auth.actualizaEmpresaGlobal(event.value);
    await this.router.navigate(['obras']);
  }

  onEmpresas() {
    this.empresasSelect.open();
  }

  async onCerrarSesion(): Promise<void> {
    await this.auth.signout();
  }

  async onReenviarEmailConfirmacion(sesion: UsuarioAuth): Promise<void> {
    if (!sesion || !sesion.usuario) {
      return;
    }

    if (this.resendEmailInFlight || this.resendEmailCountdown > 0) {
      return;
    }

    const usuario = sesion.usuario;
    const email = usuario.email;

    if (!email) {
      this.snackBar.open('No encontramos un correo para reenviar la confirmación.', 'Cerrar', {
        duration: 4000,
      });
      return;
    }

    try {
      this.resendEmailInFlight = true;
      this.startResendEmailCooldown();

      const html = this.buildReenvioHtml(usuario, sesion.empresa?.nombre);
      const nombre = usuario.nombre ?? usuario.segundoNombre ?? 'Usuario';
      const subject = `${nombre}, confirma tu correo electrónico`;

      await this.emailService.composeEmail(
        [email],
        subject,
        html,
        'Confirmación de correo',
        EmailTemplates.generico
      );
      this.snackBar.open('Enviamos un nuevo correo de activación.', undefined, { duration: 5000 });
    } catch (error) {
      console.error('Error al reenviar correo de confirmación', error);
      this.snackBar.open('Ocurrió un error al reenviar el correo. Inténtalo más tarde.', 'Cerrar', {
        duration: 5000,
      });
      this.clearResendEmailTimer();
    } finally {
      this.resendEmailInFlight = false;
    }
  }

  private buildReenvioHtml(usuario: Usuario, empresaNombre?: string): string {
    const confirmUrl = `${environment.appURL}/registro/confirmacion?token=${usuario.id}`;
    const nombreUsuario = `${usuario.nombre ?? ''} ${usuario.apellidoPaterno ?? ''}`.trim() || 'Hola';
    const empresaLinea = empresaNombre ? `<p><strong>Empresa:</strong> ${empresaNombre}</p>` : '';

    return `
      <p>${nombreUsuario},</p>
      <p>Recibimos tu solicitud para confirmar el correo registrado en Ranvou.</p>
      ${empresaLinea}
      <p>Da clic en el siguiente botón para terminar la activación:</p>
      <p style="text-align:center;margin:32px 0;">
        <a href="${confirmUrl}" class="button">Confirmar correo</a>
      </p>
      <p>Si el botón no funciona copia y pega esta liga en tu navegador:</p>
      <p>${confirmUrl}</p>
    `;
  }

  private startResendEmailCooldown(): void {
    this.resendEmailCountdown = this.resendEmailCooldownSeconds;
    this.clearResendEmailTimer();
    this.resendEmailTimer = setInterval(() => {
      this.resendEmailCountdown -= 1;
      if (this.resendEmailCountdown <= 0) {
        this.clearResendEmailTimer();
      }
    }, 1000);
  }

  private clearResendEmailTimer(): void {
    if (this.resendEmailTimer) {
      clearInterval(this.resendEmailTimer);
      this.resendEmailTimer = null;
    }
    this.resendEmailCountdown = 0;
  }
}
